import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { InstrumentTypeResponse, SideResponse } from "../../codegen-api";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { FormValidatorKeysEnum } from "../../enums/form";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { useMarginRequirements } from "../../hooks/api/margin/useMarginRequirements";
import { useOrder } from "../../hooks/api/order/useOrder";
import { useToast } from "../../hooks/toast";
import { useSFX } from "../../hooks/useSFX";
import { IPositionInfo, ITradeInfo } from "../../interfaces/TradeInfo";
import { AssetResponse } from "../../utils/asset";
import {
  getAssetFromSymbol,
  getContractPriceStep,
} from "../../utils/instruments";
import { PerpFormFieldKeyEnum } from "../TradeForm/PerpsTradeForm/form";
import BaseDetailsModal from "../shared/ViewDetailsModal/BaseDetailsModal";
import { AuthContext } from "../../contexts/AuthContext";
import { ConnectWalletContext } from "../../contexts/ConnectWalletContext";
import { IEditOrderModalProps } from "./EditOrderModal.types";
import {
  buildPositionInfo,
  buildTradeInfo,
  getTriggerPriceValidateFn,
} from "./EditOrderModal.infoBuilders";
import {
  computeEditBuyingPower,
  computeEditContractSize,
  computeMaxLeverage,
  computeTotalValue,
  computeUnfilledAmount,
  sizeToUsdValue,
  submitEditOrder,
  isUsdEditValueTooSmall,
  verifyOrderValueTooSmallImpl,
} from "./EditOrderModal.helpers";
import { renderPriceSizeInputs } from "./EditOrderModal.PriceSizeInputs";
import { renderFormContent } from "./EditOrderModal.FormContent";

/**
 * The modal responsible for removing orders
 */
function EditOrderModal({
  order,
  onHide,
  show,
  ...modalProps
}: IEditOrderModalProps) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [perpAsset, setPerpAsset] = useState<AssetResponse | undefined>();
  const { t: apiError } = useTranslation("apiErrors");
  const { accountSigningKeyState } = useContext(AuthContext);
  const { setShowConnectModal } = useContext(ConnectWalletContext);
  // DATA
  const { data: accountData } = useGetAccount();
  const { getMarketPrecision, activeOptionMarkets, activePerpMarkets } =
    useContext(MarketInstrumentContext);
  const asset = useMemo(
    () => getAssetFromSymbol(order?.instrument_name || ""),
    [order?.instrument_name]
  );
  const instrument = useMemo(() => {
    if (asset && order) {
      const markets =
        order.instrument_type === InstrumentTypeResponse.Option
          ? activeOptionMarkets
          : activePerpMarkets;
      return markets?.find((m) => m.underlying_asset === asset);
    }
    return undefined;
  }, [activeOptionMarkets, activePerpMarkets, asset, order]);
  const contractPriceStep = useMemo(
    () => getContractPriceStep(instrument),
    [instrument]
  );

  const unfilledAmount = useMemo(
    () => computeUnfilledAmount(order, contractPriceStep),
    [contractPriceStep, order?.amount, order?.filled]
  );
  // The USD figure pre-filled in USD mode, and what it was derived from.
  const usdSeed = useMemo(
    () =>
      order
        ? {
            usd: sizeToUsdValue(order.price, unfilledAmount),
            price: order.price,
            size: unfilledAmount,
          }
        : undefined,
    [order, unfilledAmount]
  );

  const { t: translateAll } = useTranslation();
  const { t } = useTranslation("app", {
    keyPrefix: "ConfirmationModal.EditOrderModal",
  });
  const { addToast } = useToast();
  const { editOrder } = useOrder();
  const { playSound } = useSFX();
  const {
    register,
    formState: { errors, touchedFields, dirtyFields, isSubmitted },
    handleSubmit,
    setValue,
    control,
    reset,
    trigger,
  } = useForm({
    mode: "onChange",
  });
  const amount = useWatch({ control, name: "amount" });
  const price = useWatch({ control, name: "price" });
  const showAmountValidation = Boolean(
    touchedFields["amount"] || dirtyFields["amount"] || isSubmitted
  );
  const triggerPrice = useWatch({
    control,
    name: PerpFormFieldKeyEnum.TRIGGER_PRICE,
  });
  const isOption = useMemo(() => !!order?.option_type, [order?.option_type]);

  // Floored to `amount_step`, as plain text: this is what the order is sent for.
  const contractSizeText = useMemo(
    () =>
      computeEditContractSize({
        amount,
        price,
        // Not option, and USDC is selected
        isUsd: !isOption && !perpAsset,
        contractPriceStep,
        seed: usdSeed,
      }),
    [amount, isOption, perpAsset, price, contractPriceStep, usdSeed]
  );
  const contractSize = Number(contractSizeText);
  const { data: marginData } = useMarginRequirements(
    {
      is_buy: order?.side === SideResponse.Buy,
      limit_price: price,
      amount: contractSizeText,
      instrument_id: Number(order?.instrument_id),
    },
    order?.order_id
  );

  // Update default asset
  useEffect(() => {
    setPerpAsset(instrument?.underlying_asset as AssetResponse);
  }, [instrument?.underlying_asset]);

  // Whenever the modal is presetned, reset state.
  useEffect(() => {
    if (show) {
      setErrorMessage(undefined);
    } else {
      // Reset form state when hiding
      reset();
    }
  }, [show, reset]);

  // Whenever an order is selected, updated the default value.
  useEffect(() => {
    if (show && order) {
      setValue(PerpFormFieldKeyEnum.TRIGGER_PRICE, order.trigger);
      setValue(PerpFormFieldKeyEnum.PRICE, order.price);
      setValue(
        PerpFormFieldKeyEnum.AMOUNT,
        !isOption && !perpAsset
          ? usdSeed?.usd
          : unfilledAmount
      );
    }
  }, [order, perpAsset, setValue, show, isOption, unfilledAmount, usdSeed]);

  // Also trigger updates whenever price is changed
  useEffect(() => {
    if (price && amount && triggerPrice) {
      trigger("price");
      trigger("amount");
      trigger(PerpFormFieldKeyEnum.TRIGGER_PRICE);
    }
  }, [price, amount, triggerPrice, trigger]);

  // Trigger checking whenever marginData changed
  useEffect(() => {
    if (marginData) {
      trigger("price");
      trigger("amount");
    }
  }, [marginData, trigger]);

  const onEditOrder = useCallback(
    () =>
      submitEditOrder({ order, editOrder, contractSize: contractSizeText, contractPriceStep, price, triggerPrice, playSound, addToast, t, onHide, apiError, setErrorMessage, setLoading }),
    [order, editOrder, contractSizeText, contractPriceStep, price, triggerPrice, playSound, addToast, t, onHide, apiError]
  );

  // USD mode: the size the typed figure turns into at submit (floored to the
  // step; the order's own size while the pre-filled figures are untouched).
  const usdContractSizeFor = useCallback(
    (usd: string) =>
      computeEditContractSize({
        amount: usd,
        price,
        isUsd: true,
        contractPriceStep,
        seed: usdSeed,
      }),
    [contractPriceStep, price, usdSeed]
  );
  const verifyOrderValueTooSmall = useCallback(
    (size: string) =>
      // With a price, a USD figure is checked by what it actually buys: the
      // figure itself is no longer snapped to that value on blur.
      !isOption && !perpAsset && Number(price) > 0
        ? !isUsdEditValueTooSmall(usdContractSizeFor(size), price)
        : verifyOrderValueTooSmallImpl(size, { isOption, perpAsset, order, instrument, price }),
    [instrument?.mark_price, isOption, order?.order_type, order?.trigger, perpAsset, price, usdContractSizeFor]
  );

  const verifyNotEnoughBalance = useCallback(() => {
    if (marginData) {
      return (
        Number(marginData.initial_margin) <=
        Number(accountData?.available_balance || 0)
      );
    }
    return true;
  }, [marginData, accountData?.available_balance]);

  // Account max leverage takes precedence before asset max leverage
  const maxLeverage = useMemo(
    () => computeMaxLeverage({ accountData, instrument }),
    [accountData?.leverages, instrument]
  );

  // 0 means "not known" (see computeEditBuyingPower) and reads as "—".
  const buyingPower = useMemo(
    () => computeEditBuyingPower({ accountData, maxLeverage, instrument }),
    [accountData, instrument, maxLeverage]
  );

  const totalValue = useMemo(
    () => computeTotalValue({ order, instrument, price, amount, contractSize }),
    [order?.order_type, order?.trigger, instrument?.mark_price, price, amount, contractSize]
  );

  // ERRORS
  const orderValueTooSmall = useMemo(() => {
    if (errors?.amount?.type === FormValidatorKeysEnum.orderValueTooSmall) {
      return true;
    }
    return false;
  }, [errors?.amount?.type]);

  const notEnoughBalanceError = useMemo(() => {
    if (errors?.amount?.type === FormValidatorKeysEnum.notEnoughBalance) {
      return true;
    }
    return false;
  }, [errors?.amount?.type]);

  const hasInputError = useMemo(
    () => Boolean(errors?.amount?.type || errors?.price?.type),
    [errors?.amount?.type, errors?.price?.type]
  );

  const hasAnyError = useMemo(() => Boolean(hasInputError), [hasInputError]);

  const tradeInfo = useMemo<ITradeInfo[]>(
    () =>
      buildTradeInfo({ order, isOption, totalValue, getMarketPrecision, asset, instrument, notEnoughBalanceError, orderValueTooSmall, marginData, t }),
    [order, isOption, totalValue, getMarketPrecision, asset, instrument?.instrument_type, notEnoughBalanceError, orderValueTooSmall, marginData?.initial_margin, t]
  );

  const triggerPriceValidateFn = useMemo(
    () => getTriggerPriceValidateFn({ order, instrument }),
    [instrument?.mark_price, order?.side, order?.stop]
  );

  const positionInfo = useMemo<IPositionInfo[]>(
    () =>
      buildPositionInfo({ order, accountData, isOption, t, asset, buyingPower, notEnoughBalanceError, marginData }),
    [accountData, asset, buyingPower, isOption, marginData?.liquidation_price, notEnoughBalanceError, order, t]
  );

  const priceSizeInputs = useMemo(
    () =>
      renderPriceSizeInputs({ isOption, contractPriceStep, register, loading, setValue, errors, verifyNotEnoughBalance, verifyOrderValueTooSmall, order, instrument, triggerPriceValidateFn, perpAsset, setPerpAsset, price, unfilledAmount, showAmountValidation, usdContractSizeFor }),
    [instrument?.underlying_asset, errors, isOption, loading, register, setValue, verifyNotEnoughBalance, verifyOrderValueTooSmall, perpAsset, price, contractPriceStep, order, instrument?.mark_price, triggerPriceValidateFn, unfilledAmount, showAmountValidation, usdContractSizeFor]
  );

  const form = useMemo(
    () =>
      renderFormContent({ order, handleSubmit, onEditOrder, t, translateAll, priceSizeInputs, tradeInfo, positionInfo, accountSigningKeyState, loading, hasAnyError, errorMessage, setShowConnectModal }),
    [order, handleSubmit, onEditOrder, t, translateAll, priceSizeInputs, tradeInfo, positionInfo, accountSigningKeyState, loading, hasAnyError, errorMessage, setShowConnectModal]
  );

  if (!order) {
    return null;
  }

  return (
    //@ts-ignore
    <BaseDetailsModal
      instrument={
        order
          ? {
              instrument_name: order.instrument_name,
              expiry: order.expiry,
              strike: order.strike,
              markPrice: instrument?.mark_price,
            }
          : undefined
      }
      show={!!show}
      onHide={onHide}
      {...modalProps}
    >
      {form}
    </BaseDetailsModal>
  );
}

export default memo(EditOrderModal);
